import { useSyncExternalStore } from 'react';
import {
  clearDailyReportOfflineData,
  countDailyReportOfflineItems,
} from '@/app/pages/work/daily-reports/use-offline-draft';
import {
  clearRiggingDiagramStorage,
  countRiggingDiagramDrafts,
} from '@/components/safety/rigging-canvas/persistence';
import { __clearAllKioskCredentials } from '@/lib/offline/kiosk-credential-vault';
import { clearOutbox, countQueuedSignatures } from '@/lib/offline/outbox';
import { clearAllCachedSigningTokens } from '@/lib/offline/shift-signing-token-cache';
import { clearCachedApiResponses } from '@/lib/offline/service-worker-cache';

const API_RESPONSE_CACHE_TIMEOUT_MS = 1000;
const OFFLINE_QUEUE_CACHE_NAME = 'h3ight-offline-queue';

export interface OfflineSessionOwner {
  userId: number;
  orgId: number | null;
}

export interface OfflineWorkCount {
  count: number;
  uncertain: boolean;
}

export interface OwnedRecord {
  ownerUserId?: number | null;
  ownerOrgId?: number | null;
}

export type DiscardReason = 'logout' | 'switch-org';

type DiscardConfirmer = (info: OfflineWorkCount, reason: DiscardReason) => Promise<boolean>;

const listeners = new Set<() => void>();
let sessionOwner: OfflineSessionOwner | null = null;
let ownerPublished = false;
let discardConfirmer: DiscardConfirmer | null = null;
let persistChain: Promise<void> = Promise.resolve();

function notify() {
  listeners.forEach((listener) => {
    try {
      listener();
    } catch {
      // Ignore listener failures.
    }
  });
}

function ownersEqual(left: OfflineSessionOwner | null, right: OfflineSessionOwner | null): boolean {
  return left?.userId === right?.userId && left?.orgId === right?.orgId;
}

function postSessionOwner(owner: OfflineSessionOwner | null) {
  if (typeof navigator === 'undefined' || !navigator.serviceWorker) return;

  const message = { type: 'SET_OFFLINE_SESSION_OWNER', owner };
  try {
    navigator.serviceWorker.controller?.postMessage(message);
  } catch {
    // Service worker messaging is best effort.
  }
  try {
    void navigator.serviceWorker.ready
      .then((registration) => registration.active?.postMessage(message))
      .catch(() => undefined);
  } catch {
    // Service worker messaging is best effort.
  }
}

async function writeSessionOwner(owner: OfflineSessionOwner | null): Promise<void> {
  if (typeof window === 'undefined' || typeof window.caches?.open !== 'function') return;
  const cache = await window.caches.open('h3ight-offline-queue');
  const request = new Request('/__h3ight/offline-session-owner');
  if (owner === null) {
    await cache.delete(request);
  } else {
    await cache.put(request, new Response(JSON.stringify({ owner }), {
      headers: { 'Content-Type': 'application/json' },
    }));
  }
}

function persistSessionOwner(owner: OfflineSessionOwner | null) {
  persistChain = persistChain.then(() => writeSessionOwner(owner)).catch(() => undefined);
}

if (typeof navigator !== 'undefined' && navigator.serviceWorker) {
  try {
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      postSessionOwner(sessionOwner);
    });
  } catch {
    // Service worker lifecycle events are best effort.
  }
}

export function getOfflineSessionOwner(): OfflineSessionOwner | null {
  return sessionOwner;
}

export function setOfflineSessionOwner(owner: OfflineSessionOwner | null): void {
  if (ownerPublished && ownersEqual(sessionOwner, owner)) return;
  ownerPublished = true;
  sessionOwner = owner;
  notify();
  postSessionOwner(owner);
  persistSessionOwner(owner);
}

export async function confirmOfflineSessionOwner(owner: OfflineSessionOwner | null): Promise<void> {
  try {
    const changed = !ownersEqual(sessionOwner, owner);
    ownerPublished = true;
    sessionOwner = owner;
    if (changed) notify();
    persistSessionOwner(owner);

    if (typeof navigator === 'undefined' || !navigator.serviceWorker || typeof MessageChannel === 'undefined') {
      return;
    }

    await new Promise<void>((resolve) => {
      let finished = false;
      let discoveryFinished = false;
      let pendingAcks = 0;
      const workers = new Set<ServiceWorker>();
      const ports = new Set<MessagePort>();
      const timeoutId = globalThis.setTimeout(finish, 1000);

      function finish() {
        if (finished) return;
        finished = true;
        globalThis.clearTimeout(timeoutId);
        ports.forEach((port) => {
          try {
            port.close();
          } catch {
            return;
          }
        });
        ports.clear();
        resolve();
      }

      function finishWhenAcknowledged() {
        if (discoveryFinished && pendingAcks === 0) finish();
      }

      function postToWorker(worker: ServiceWorker | null | undefined) {
        if (finished || !worker || workers.has(worker)) return;
        workers.add(worker);
        const channel = new MessageChannel();
        const port = channel.port1;
        ports.add(port);
        pendingAcks += 1;
        port.onmessage = (event: MessageEvent) => {
          if (event.data?.type !== 'OFFLINE_SESSION_OWNER_SET') return;
          pendingAcks -= 1;
          port.close();
          ports.delete(port);
          finishWhenAcknowledged();
        };
        try {
          worker.postMessage({ type: 'SET_OFFLINE_SESSION_OWNER', owner }, [channel.port2]);
        } catch {
          pendingAcks -= 1;
          port.close();
          ports.delete(port);
        }
      }

      try {
        const serviceWorker = navigator.serviceWorker;
        postToWorker(serviceWorker.controller);
        Promise.resolve()
          .then(() => serviceWorker.getRegistration())
          .then((registration) => {
            postToWorker(registration?.active);
          })
          .catch(() => undefined)
          .finally(() => {
            discoveryFinished = true;
            finishWhenAcknowledged();
          });
      } catch {
        discoveryFinished = true;
        finishWhenAcknowledged();
      }
    });
  } catch {
    return;
  }
}

export function subscribeOfflineSessionOwner(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useOfflineSessionOwner(): OfflineSessionOwner | null {
  return useSyncExternalStore(
    subscribeOfflineSessionOwner,
    getOfflineSessionOwner,
    () => null,
  );
}

export function isOwnedBy(record: OwnedRecord, owner: OfflineSessionOwner | null): boolean {
  if (!owner || record.ownerUserId == null) return false;
  return record.ownerUserId === owner.userId && (record.ownerOrgId ?? null) === owner.orgId;
}

export function holdOfflineReplay(): () => void {
  const previousOwner = getOfflineSessionOwner();
  setOfflineSessionOwner(null);
  return () => setOfflineSessionOwner(previousOwner);
}

function countServiceWorkerQueue(): Promise<OfflineWorkCount> {
  if (typeof indexedDB === 'undefined') return Promise.resolve({ count: 0, uncertain: false });
  return new Promise((resolve) => {
    let settled = false;
    let db: IDBDatabase | null = null;
    const finish = (result: OfflineWorkCount) => {
      if (settled) return;
      settled = true;
      globalThis.clearTimeout(timeoutId);
      db?.close();
      resolve(result);
    };
    const timeoutId = globalThis.setTimeout(
      () => finish({ count: 0, uncertain: true }),
      1500,
    );
    try {
      const request = indexedDB.open('h3ight-offline');
      request.onerror = () => finish({ count: 0, uncertain: true });
      request.onblocked = () => finish({ count: 0, uncertain: true });
      request.onsuccess = () => {
        db = request.result;
        if (settled) {
          db.close();
          return;
        }
        if (!db.objectStoreNames.contains('queue')) {
          finish({ count: 0, uncertain: false });
          return;
        }
        try {
          const transaction = db.transaction('queue', 'readonly');
          const countRequest = transaction.objectStore('queue').count();
          countRequest.onsuccess = () => finish({ count: countRequest.result, uncertain: false });
          countRequest.onerror = () => finish({ count: 0, uncertain: true });
          transaction.onabort = () => finish({ count: 0, uncertain: true });
          transaction.onerror = () => finish({ count: 0, uncertain: true });
        } catch {
          finish({ count: 0, uncertain: true });
        }
      };
    } catch {
      finish({ count: 0, uncertain: true });
    }
  });
}

function countOfflineSource(count: () => number | Promise<number>): Promise<OfflineWorkCount> {
  return Promise.resolve()
    .then(count)
    .then((value) => ({ count: value, uncertain: false }))
    .catch(() => ({ count: 0, uncertain: true }));
}

function withApiCacheTimeout<T>(
  operation: () => Promise<T>,
): Promise<{ ok: true; value: T } | { ok: false }> {
  return new Promise((resolve) => {
    let settled = false;
    const finish = (result: { ok: true; value: T } | { ok: false }) => {
      if (settled) return;
      settled = true;
      globalThis.clearTimeout(timeoutId);
      resolve(result);
    };
    const timeoutId = globalThis.setTimeout(
      () => finish({ ok: false }),
      API_RESPONSE_CACHE_TIMEOUT_MS,
    );
    Promise.resolve()
      .then(operation)
      .then(
        (value) => finish({ ok: true, value }),
        () => finish({ ok: false }),
      );
  });
}

async function hasApiResponseCacheEntries(): Promise<boolean> {
  const cacheNames = await caches.keys();
  const cacheResults = await Promise.all(
    cacheNames
      .filter((name) => name !== OFFLINE_QUEUE_CACHE_NAME)
      .map(async (name) => {
        const cache = await caches.open(name);
        const requests = await cache.keys();
        return requests.some((request) => new URL(request.url).pathname.startsWith('/api/'));
      }),
  );
  return cacheResults.some(Boolean);
}

function countApiResponseCache(): Promise<OfflineWorkCount> {
  if (typeof caches === 'undefined') {
    return Promise.resolve({ count: 0, uncertain: false });
  }
  return withApiCacheTimeout(async () => {
    const cacheNames = await caches.keys();
    await Promise.all(
      cacheNames
        .filter((name) => name !== OFFLINE_QUEUE_CACHE_NAME)
        .map(async (name) => {
          const cache = await caches.open(name);
          await cache.keys();
        }),
    );
  }).then(({ ok }) => ({ count: 0, uncertain: !ok }));
}

export async function countUnsyncedOfflineWork(): Promise<OfflineWorkCount> {
  const [
    outboxCount,
    serviceWorkerCount,
    dailyReportCount,
    riggingDiagramCount,
    apiResponseCacheCount,
  ] = await Promise.all([
    countOfflineSource(() => (typeof indexedDB === 'undefined' ? 0 : countQueuedSignatures())),
    countServiceWorkerQueue(),
    countOfflineSource(countDailyReportOfflineItems),
    countOfflineSource(countRiggingDiagramDrafts),
    countApiResponseCache(),
  ]);
  return {
    count: outboxCount.count
      + serviceWorkerCount.count
      + dailyReportCount.count
      + riggingDiagramCount.count
      + apiResponseCacheCount.count,
    uncertain: outboxCount.uncertain
      || serviceWorkerCount.uncertain
      || dailyReportCount.uncertain
      || riggingDiagramCount.uncertain
      || apiResponseCacheCount.uncertain,
  };
}

async function clearServiceWorkerQueue(): Promise<void> {
  if (typeof indexedDB === 'undefined') return;
  await new Promise<void>((resolve) => {
    let db: IDBDatabase | null = null;
    try {
      const request = indexedDB.open('h3ight-offline');
      request.onerror = () => resolve();
      request.onblocked = () => resolve();
      request.onsuccess = () => {
        db = request.result;
        const stores = ['queue', 'conflicts'].filter((store) => db?.objectStoreNames.contains(store));
        if (!stores.length) {
          db.close();
          resolve();
          return;
        }
        try {
          const transaction = db.transaction(stores, 'readwrite');
          stores.forEach((store) => transaction.objectStore(store).clear());
          transaction.oncomplete = () => {
            db?.close();
            resolve();
          };
          transaction.onerror = transaction.onabort = () => {
            db?.close();
            resolve();
          };
        } catch {
          db?.close();
          resolve();
        }
      };
    } catch {
      resolve();
    }
  });
}

function clearServiceWorkerQueueFromWorker(): Promise<void> {
  const controller = typeof navigator === 'undefined' ? null : navigator.serviceWorker?.controller;
  if (!controller || typeof MessageChannel === 'undefined') {
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    const channel = new MessageChannel();
    const timeoutId = window.setTimeout(() => {
      channel.port1.close();
      channel.port2.close();
      resolve();
    }, 1000);
    channel.port1.onmessage = (event: MessageEvent) => {
      if (event.data?.type === 'OFFLINE_QUEUE_CLEARED' || event.data?.type === 'OFFLINE_QUEUE_CLEAR_FAILED') {
        window.clearTimeout(timeoutId);
        channel.port1.close();
        channel.port2.close();
        resolve();
      }
    };
    try {
      controller.postMessage({ type: 'CLEAR_OFFLINE_QUEUE' }, [channel.port2]);
    } catch {
      window.clearTimeout(timeoutId);
      channel.port1.close();
      channel.port2.close();
      resolve();
    }
  });
}

export async function clearSessionOfflineData(): Promise<void> {
  setOfflineSessionOwner(null);
  await Promise.allSettled([
    Promise.resolve().then(() => clearOutbox()),
    Promise.resolve().then(() => clearAllCachedSigningTokens()),
    Promise.resolve().then(() => __clearAllKioskCredentials()),
    Promise.resolve().then(() => clearDailyReportOfflineData()),
    Promise.resolve().then(() => clearRiggingDiagramStorage()),
    clearServiceWorkerQueue(),
    clearServiceWorkerQueueFromWorker(),
  ]);
}

export async function clearSessionApiResponseCache(): Promise<void> {
  try {
    await withApiCacheTimeout(() =>
      Promise.resolve().then(clearCachedApiResponses).catch(() => undefined),
    );
    if (typeof caches === 'undefined') return;

    const scan = await withApiCacheTimeout(hasApiResponseCacheEntries);
    if (scan.ok && !scan.value) return;

    const cacheNames = await withApiCacheTimeout(() => caches.keys());
    if (!cacheNames.ok) {
      // The rotated _cache_scope in clearCachedApiResponses prevents the next account from matching old entries.
      return;
    }
    await Promise.all(
      cacheNames.value
        .filter((name) => name !== OFFLINE_QUEUE_CACHE_NAME)
        .map(async (name) => {
          const scanCache = await withApiCacheTimeout(async () => {
            const cache = await caches.open(name);
            const requests = await cache.keys();
            return requests.some((request) => new URL(request.url).pathname.startsWith('/api/'));
          });
          if (!scanCache.ok || scanCache.value) {
            await withApiCacheTimeout(() => caches.delete(name));
          }
        }),
    );
  } catch {
    // Cache Storage failures must not block logout or organization switching.
  }
}

export function registerOfflineDiscardConfirmer(fn: DiscardConfirmer): () => void {
  discardConfirmer = fn;
  return () => {
    if (discardConfirmer === fn) discardConfirmer = null;
  };
}

export function getOfflineDiscardWarning({ count, uncertain }: OfflineWorkCount): string {
  if (uncertain && count > 0) {
    return `At least ${count} item(s) saved on this device have not synced yet, and some offline data could not be checked. If you continue it will be permanently deleted from this device.`;
  }
  if (uncertain) {
    return 'Some offline data on this device could not be checked and may include unsynced work. If you continue it will be permanently deleted from this device.';
  }
  return `${count} item(s) saved on this device have not synced yet. If you continue they will be permanently deleted from this device.`;
}

export async function confirmDiscardOfflineWork(reason: DiscardReason): Promise<boolean> {
  const info = await countUnsyncedOfflineWork();
  if (info.count === 0 && !info.uncertain) return true;
  if (discardConfirmer) return discardConfirmer(info, reason);
  return typeof window.confirm === 'function'
    ? window.confirm(getOfflineDiscardWarning(info))
    : false;
}

export class OfflineDiscardCancelledError extends Error {
  constructor() {
    super('Discarding offline work was cancelled');
    this.name = 'OfflineDiscardCancelledError';
  }
}

export function isOfflineDiscardCancelled(error: unknown): boolean {
  return error instanceof OfflineDiscardCancelledError;
}
